import React, { useEffect, useState } from "react";
import { Download, Smartphone, Zap, WifiOff, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { triggerInstallPrompt, isStandaloneMode, isIos } from "@/lib/pwa";
import { toast } from "sonner";

/**
 * Login-page install prompt (Iter-50).
 *
 * Behavior (option B — persistent nudge until user installs):
 *   - Hidden if the app is already running in standalone / PWA mode.
 *   - Hidden if the platform can neither fire `beforeinstallprompt`
 *     (Android/Chrome/Edge desktop) nor offer the iOS Add-to-Home-Screen path.
 *   - Otherwise, opens automatically ~600ms after Login mounts, on every
 *     visit. The user can dismiss it (Not now) — dismissal is scoped to
 *     the current tab session so it doesn't harass mid-typing, but a new
 *     page load or new tab surfaces it again.
 *   - "Install app" fires the native prompt on Chromium; on iOS Safari it
 *     swaps in step-by-step instructions.
 *
 * Storage: intentionally NO localStorage/cookie flag — the user chose
 * "persistent nudge" so every fresh visit re-prompts.
 */
export default function LoginInstallPrompt() {
  const [installable, setInstallable] = useState(false);
  const [standalone, setStandalone] = useState(false);
  const [open, setOpen] = useState(false);
  const [iosMode, setIosMode] = useState(false);
  const [dismissed, setDismissed] = useState(false); // guards auto re-open after user closes
  const iosDevice = isIos();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sa = isStandaloneMode();
    setStandalone(sa);
    if (sa) return;

    const onInstallable = () => {
      setInstallable(true);
      // Late-firing beforeinstallprompt: open the modal unless the user
      // has already dismissed it this session.
      setOpen((prev) => (prev || dismissed ? prev : true));
    };
    const onInstalled = () => {
      setStandalone(true);
      setOpen(false);
    };
    window.addEventListener("summit-air:installable", onInstallable);
    window.addEventListener("appinstalled", onInstalled);

    if (typeof window !== "undefined" && window.__summitInstallable) {
      setInstallable(true);
    }

    // Auto-open ~600ms after mount unless user dismissed already.
    const t = window.setTimeout(() => {
      if (isStandaloneMode() || dismissed) return;
      if (window.__summitInstallable || iosDevice) {
        setOpen(true);
      }
    }, 600);

    return () => {
      window.removeEventListener("summit-air:installable", onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
      window.clearTimeout(t);
    };
  }, [iosDevice, dismissed]);

  if (standalone) return null;
  if (!installable && !iosDevice) return null;

  const onDismiss = () => {
    setDismissed(true);
    setOpen(false);
  };

  const onInstall = async () => {
    if (iosDevice && !installable) {
      setIosMode(true);
      return;
    }
    const outcome = await triggerInstallPrompt();
    if (outcome === "accepted") {
      toast.success("Installing SA…");
      setStandalone(true);
      setOpen(false);
    } else if (outcome === "dismissed") {
      toast.info("Install cancelled — you can try again from the nav bar after signing in.");
      onDismiss();
    } else if (outcome === "unavailable") {
      if (iosDevice) setIosMode(true);
      else {
        toast.info("Your browser doesn't offer a one-click install right now.");
        onDismiss();
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onDismiss(); else setOpen(true); }}>
      <DialogContent className="max-w-md" data-testid="login-install-modal">
        <DialogHeader>
          <div className="mx-auto mb-3 w-12 h-12 rounded-2xl bg-summit-glacier flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-summit-primary" />
          </div>
          <DialogTitle className="text-center text-xl font-display tracking-tight">
            {iosMode ? "Add SA to your Home Screen" : "Install SA"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {iosMode
              ? "Safari doesn't offer a one-click install — three quick taps get you there."
              : "Turn SA into a real app on your device. It's fast, works offline, and lives on your home screen just like a native app."}
          </DialogDescription>
        </DialogHeader>

        {iosMode ? (
          <ol className="space-y-3 text-sm mt-1" data-testid="login-install-ios-steps">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-summit-glacier text-summit-primary flex items-center justify-center font-mono text-xs shrink-0">1</span>
              <div>Tap the <span className="font-mono">Share</span> icon (square with an up-arrow) at the bottom of Safari.</div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-summit-glacier text-summit-primary flex items-center justify-center font-mono text-xs shrink-0">2</span>
              <div>Scroll and tap <span className="font-mono">Add to Home Screen</span>.</div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-summit-glacier text-summit-primary flex items-center justify-center font-mono text-xs shrink-0">3</span>
              <div>Tap <span className="font-mono">Add</span> in the top-right — SA will appear as its own app icon.</div>
            </li>
          </ol>
        ) : (
          <ul className="space-y-2 mt-2" data-testid="login-install-benefits">
            <li className="flex items-start gap-3 text-sm">
              <Zap className="w-4 h-4 mt-0.5 text-summit-primary shrink-0" />
              <span>One-tap launch — no more searching for the tab in your browser.</span>
            </li>
            <li className="flex items-start gap-3 text-sm">
              <WifiOff className="w-4 h-4 mt-0.5 text-summit-primary shrink-0" />
              <span>Clock in even when you&apos;re out of signal — your entries sync when you&apos;re back online.</span>
            </li>
            <li className="flex items-start gap-3 text-sm">
              <Download className="w-4 h-4 mt-0.5 text-summit-primary shrink-0" />
              <span>Push notifications for approvals, delegations, and time-off updates.</span>
            </li>
          </ul>
        )}

        <DialogFooter className="mt-3 flex-row justify-between gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={onDismiss}
            data-testid="login-install-dismiss"
          >
            <X className="w-4 h-4 mr-1.5" /> Not now
          </Button>
          {iosMode ? (
            <Button
              onClick={() => setOpen(false)}
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              data-testid="login-install-ios-done"
            >
              Got it
            </Button>
          ) : (
            <Button
              onClick={onInstall}
              className="bg-summit-primary hover:bg-summit-primary-soft text-white"
              data-testid="login-install-confirm"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Install app
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
